import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useReveal } from "../../hooks/useReveal";
import { easing } from "../../lib/animations";
import { ScrambleTextControlled } from "./ScrambleText";
import { ActionButton } from "./ActionButton";

const TOPICS = [
    {
        date: "2026.02.14",
        category: "Collection",
        section: "KOL-01 · Signal",
        title:
            "Foundational graphics. First silhouettes. Built the visual grammar we still speak.",
        video: "/assets/yk/mockup1.mp4",
    },
    {
        date: "2026.01.30",
        category: "Collection",
        section: "KOL-02 · Static",
        title:
            "Core tees. Expanded colour system. Noise in every weave.",
        video: "/assets/yk/mockup2.mp4",
    },
    {
        date: "2026.01.12",
        category: "Object Study",
        section: "Signal · 003",
        title:
            "Heavy cotton, washed twice. A material that does not perform. It simply outlives the trend cycle.",
        video: "/assets/yk/mockup3.mp4",
    },
];

/**
 * Hover-play video — paused on its first frame by default, plays from 0 on hover,
 * pauses and rewinds on mouse leave so the next hover always starts fresh.
 */
const HoverVideo = ({ src, testId, hover }) => {
    const ref = useRef(null);

    // On mount: load + nudge currentTime so the first frame paints (browsers with
    // preload="metadata" otherwise leave the <video> rectangle empty)
    React.useEffect(() => {
        const v = ref.current;
        if (!v) return;
        const showFirstFrame = () => {
            try {
                v.currentTime = 0.05;
            } catch (e) {
                /* metadata not ready yet */
            }
        };
        if (v.readyState >= 1) {
            showFirstFrame();
        } else {
            v.addEventListener("loadedmetadata", showFirstFrame, { once: true });
        }
        return () => v.removeEventListener("loadedmetadata", showFirstFrame);
    }, []);

    React.useEffect(() => {
        const v = ref.current;
        if (!v) return;
        if (hover) {
            try {
                v.currentTime = 0;
            } catch (e) {
                /* metadata may not be ready */
            }
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        } else {
            try {
                v.pause();
                v.currentTime = 0.05;
            } catch (e) {
                /* ignore */
            }
        }
    }, [hover]);

    return (
        <video
            ref={ref}
            src={src}
            muted
            playsInline
            loop
            preload="auto"
            data-testid={testId}
            draggable={false}
            style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                filter: hover
                    ? "grayscale(0.05) contrast(1.04) saturate(1.04) brightness(0.98)"
                    : "grayscale(1) contrast(1.05) brightness(0.9)",
                transform: hover ? "scale(1.02)" : "scale(1)",
                transition:
                    "filter 1.2s cubic-bezier(0.65, 0, 0.35, 1), transform 1.6s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
        />
    );
};

const TopicRow = ({ t, i }) => {
    const [ref, inView] = useReveal({ threshold: 0.15 });
    const [hover, setHover] = useState(false);

    return (
        <motion.a
            ref={ref}
            href="#topics"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.95, delay: i * 0.05, ease: easing.primary }}
            data-testid={`topic-card-${i}`}
            className="topic-row"
            style={{
                display: "grid",
                gridTemplateColumns: "minmax(360px, 1fr) 1.4fr",
                gap: "clamp(40px, 5vw, 96px)",
                padding: "clamp(36px, 5vw, 56px) 0",
                borderTop: i === 0 ? "1px solid var(--line)" : undefined,
                borderBottom: "1px solid var(--line)",
                textDecoration: "none",
                color: "inherit",
                cursor: "pointer",
                alignItems: "center",
            }}
        >
            <div style={{ aspectRatio: "16 / 10", overflow: "hidden", width: "100%", background: "#0c0c0c" }}>
                <HoverVideo
                    src={t.video}
                    testId={`topic-video-${i}`}
                    hover={hover}
                />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 18,
                        flexWrap: "wrap",
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 11,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "var(--ink)",
                    }}
                >
                    <span style={{ fontWeight: 500 }}>{t.date}</span>
                    <span
                        style={{
                            display: "inline-flex",
                            padding: "3px 10px 4px",
                            border: "1px solid currentColor",
                            borderRadius: 2,
                            fontSize: 10,
                            letterSpacing: "0.16em",
                        }}
                    >
                        {t.category}
                    </span>
                    <span style={{ opacity: 0.55, letterSpacing: "0.04em", textTransform: "none" }}>
                        {t.section}
                    </span>
                </div>

                <h3
                    style={{
                        margin: 0,
                        fontFamily: "'Space Grotesk', 'Manrope', sans-serif",
                        fontWeight: 500,
                        fontSize: "clamp(22px, 2.6vw, 36px)",
                        lineHeight: 1.22,
                        letterSpacing: "-0.015em",
                        color: "var(--ink)",
                        maxWidth: "34ch",
                    }}
                >
                    <ScrambleTextControlled
                        text={t.title}
                        active={hover}
                        duration={320}
                        stagger={6}
                    />
                </h3>

                <div style={{ display: "flex", justifyContent: "flex-end", opacity: 0.7 }}>
                    <svg width="22" height="22" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M1 0V6C0.999976 7 1.1 8.5 3.5 8.5C5.9 8.5 10.5 8.5 11.5 8.5" stroke="currentColor" />
                        <path d="M8 4L12.5 8.5L8 13" stroke="currentColor" />
                    </svg>
                </div>
            </div>
        </motion.a>
    );
};

const AnimatedHeading = ({ text, inView, baseDelay = 0 }) => (
    <span aria-label={text} style={{ display: "inline-block", overflow: "hidden" }}>
        <span style={{ display: "inline-block" }}>
            {text.split("").map((c, i) => (
                <motion.span
                    key={`${c}-${i}`}
                    style={{ display: "inline-block", whiteSpace: "pre" }}
                    initial={{ y: "115%" }}
                    animate={inView ? { y: "0%" } : { y: "115%" }}
                    transition={{ duration: 0.7, delay: baseDelay + i * 0.032, ease: easing.enter }}
                >
                    {c === " " ? "\u00A0" : c}
                </motion.span>
            ))}
        </span>
    </span>
);

export const Topics = () => {
    const [headRef, headIn] = useReveal({ threshold: 0.3 });

    return (
        <section
            className="topics-block"
            id="topics"
            data-testid="topics-section"
            style={{
                background: "var(--bg)",
                color: "var(--ink)",
                padding: "clamp(80px, 12vw, 180px) clamp(24px, 5vw, 56px)",
            }}
        >
            <div
                ref={headRef}
                style={{
                    marginBottom: "clamp(32px, 5vw, 64px)",
                    display: "flex",
                    alignItems: "baseline",
                    gap: 24,
                    flexWrap: "wrap",
                }}
            >
                <h2
                    style={{
                        margin: 0,
                        fontFamily: "'Space Grotesk', Manrope, sans-serif",
                        fontWeight: 700,
                        fontSize: "clamp(40px, 7vw, 96px)",
                        letterSpacing: "-0.035em",
                        lineHeight: 1,
                        textTransform: "uppercase",
                    }}
                >
                    <AnimatedHeading text="Signal Archive" inView={headIn} />
                </h2>
                <span
                    style={{
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 12,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: "var(--ink-soft)",
                        opacity: 0.55,
                    }}
                >
                    <AnimatedHeading text="(Manifestos · Observations)" inView={headIn} baseDelay={0.3} />
                </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
                {TOPICS.map((t, i) => (
                    <TopicRow key={i} t={t} i={i} />
                ))}
            </div>

            <div style={{ marginTop: "clamp(48px, 6vw, 80px)", display: "flex", justifyContent: "center" }}>
                <ActionButton href="#topics" color="var(--ink)" arrow="right" testId="topics-view-more">
                    READ THE ARCHIVE
                </ActionButton>
            </div>
        </section>
    );
};

export default Topics;
